/**
 * A contagem regressiva do fim da reunião (a duração máxima do plano, `otto.meeting_max_minutes`).
 *
 * O servidor grava o prazo no metadado da sala do LiveKit (`{ "meetingEndsAt": "<ISO>" }`) quando a reunião começa, e todo
 * participante, atendente ou convidado, o lê. Faltando 5 minutos ou menos, a tela mostra o tempo que resta e NÃO o esconde mais
 * até a chamada acabar (zerou: continua em 00:00 até a sala fechar). Reunião sem limite de duração não tem prazo, e não aparece
 * nada. Regras puras, sem React.
 */

/** A contagem aparece quando falta isto (5 minutos) ou menos. É o mesmo aviso que o servidor dispara. */
export const COUNTDOWN_WINDOW_SECONDS = 300;

/** No último minuto a contagem fica vermelha. */
export const COUNTDOWN_URGENT_SECONDS = 60;

/**
 * O instante (ms) em que a reunião acaba, a partir do metadado da sala; `null` se não há prazo ou o metadado não é entendido.
 * @param {string | null | undefined} metadata
 * @returns {number | null}
 */
export function parseMeetingEndsAt(metadata) {
    if (typeof metadata !== 'string' || metadata.length === 0) return null;
    try {
        const parsed = JSON.parse(metadata);
        const ms = typeof parsed?.meetingEndsAt === 'string' ? Date.parse(parsed.meetingEndsAt) : NaN;
        return Number.isFinite(ms) ? ms : null;
    } catch {
        return null;
    }
}

/**
 * @param {number | null} endsAtMs
 * @param {number} nowMs
 * @returns {{ visible: false } | { visible: true, remainingSeconds: number, urgent: boolean }}
 */
export function countdownState(endsAtMs, nowMs) {
    if (endsAtMs == null) return { visible: false };
    const remaining = Math.ceil((endsAtMs - nowMs) / 1000);
    if (remaining > COUNTDOWN_WINDOW_SECONDS) return { visible: false };
    const remainingSeconds = Math.max(0, remaining);
    return { visible: true, remainingSeconds, urgent: remainingSeconds <= COUNTDOWN_URGENT_SECONDS };
}

/** "mm:ss" (a contagem nunca passa de 5 minutos, mas formata horas se um dia passar). */
export function formatCountdown(totalSeconds) {
    const s = Math.max(0, Math.floor(totalSeconds));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const pad = (n) => String(n).padStart(2, '0');
    return h > 0 ? `${h}:${pad(m)}:${pad(s % 60)}` : `${pad(m)}:${pad(s % 60)}`;
}

/**
 * O que um leitor de tela ouve. Muda só de minuto em minuto e no último minuto (o cronômetro em si não é anunciado a cada
 * segundo). `null` enquanto a contagem não aparece.
 * @param {ReturnType<typeof countdownState>} state
 * @returns {string | null}
 */
export function countdownAnnouncement(state) {
    if (!state.visible) return null;
    if (state.remainingSeconds === 0) return 'A reunião chegou ao fim.';
    if (state.urgent) return 'A reunião termina em menos de 1 minuto.';
    const minutes = Math.ceil(state.remainingSeconds / 60);
    return `A reunião termina em ${minutes} ${minutes === 1 ? 'minuto' : 'minutos'}.`;
}
